import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Grid, TextField } from '@material-ui/core';
import { verifyDuplicateTaskGroupName } from '../../services/validation/validation.service';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import ActionButtons from './ActionButtons';
import EditButton from './EditButton.component';
import DeleteButton from './DeleteButton.component';
import AddSessionItemButton from './NewItemButton.component';
import {
  createExamTaskGroup,
  getCanSessionBeClosed,
  getCanSessionBeOpen,
  modifyExamTaskGroup,
} from '../../services/exams/sessions/sessions.service';
import {
  CanSessionBeSetToOpen,
  CanSessionBeSetToOpenWithZwo,
  DetailsProps,
  sessionValidationMessagesNoZwo,
  sessionValidationMessagesWithZwo,
  CanSessionBeSetToOpenNoZwo,
  sessionValidationMessage,
  CanSessionBeSetToClosed,
  sessionToBeClosedValidationMessages,
  isControllProcess,
  isControllProcessMessage,
} from './DetailsProps.types';
import { BreadcrumbLevel } from '../DialogBreadcrumbs/DialogBreadcrumbs.types';
import useExamSessionValidation from '../../hooks/useExamSessionValidation';
import { sessionStatusType, ITaskGroupDetails, IIdResult } from '../../models/session.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { AxiosResponse } from 'axios';
import { NewTreeItemId, WorkMode } from '../SessionTree/SessionTree.types';
import { useStyles as sessionDetails } from './SessionDetails.styles';
import { useStyles, DateTextField } from './TaskGroupDetails.styles';
import { Prompt } from '../Prompt/Prompt.component';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import useSessionUser from '../../hooks/useSessionUser';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import { format } from 'date-fns';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import plLocale from 'date-fns/locale/pl';
import { DatePicker } from '@mui/x-date-pickers';
import { blockIconForInput } from './BlockItemForInput';

interface TaskGroupDetailsProps extends DetailsProps {
  data: ITaskGroupDetails;
  isReadOnly: () => boolean;
  onDelete: () => void;
}

const TaskGroupDetails: React.FC<TaskGroupDetailsProps> = (props) => {
  const classes = useStyles();
  const sessionDetailsClasses = sessionDetails();
  const { t } = useTranslation();
  const { userAssessmentSessionInfo } = useSessionUser();

  const [canSessionBeOpen, setCanSessionBeOpen] = useState<CanSessionBeSetToOpen | undefined>(undefined);
  const [canSessionBeClosed, setCanSessionBeClosed] = useState<CanSessionBeSetToClosed | undefined>(undefined);
  const [areAllCasesTrue, setAreAllCasesTrue] = useState<boolean>(false);
  const [areAllCasesTrueNoZwo, setAreAllCasesTrueNoZwo] = useState<boolean>(false);
  const [areAllCasesTrueControllProcess, setAreAllCasesTrueControllProcess] = useState<boolean>(false);
  const [assesmentGroupExist, setAssesmentGroupExist] = useState<boolean>();
  const [canSessionBeOpenWithZWO, setCanSessionBeOpenWithZWO] = useState<CanSessionBeSetToOpenWithZwo | undefined>(
    undefined,
  );
  const [canSessionBeOpenNoZWO, setCanSessionBeOpenNoZwo] = useState<CanSessionBeSetToOpenNoZwo | undefined>(undefined);
  const [isControlProccessAssigned, setIsControlProccessAssigned] = useState<boolean>(false); //TODO add logic after backend connection
  const [canSessionBeOpenWithControllProcess, setCanSessionBeOpenWithControllProcess] = useState<
    isControllProcess | undefined
  >(undefined); //TODO add logic after backed connection

  const { openAlert, showLoader } = useLayoutContext();

  const [taskGroupName, setTaskGroupName] = useState<string>(props.data.name ?? '');
  const [taskGroupDateFrom, setTaskGroupDateFrom] = useState<Date | null>(
    props.data.assessmentSession.dateFrom ? new Date(props.data.assessmentSession.dateFrom) : null,
  );
  const [isDateFromError, setIsDateFromError] = useState<boolean>(false);
  const [taskGroupDateTo, setTaskGroupDateTo] = useState<Date | null>(
    props.data.assessmentSession.dateTo ? new Date(props.data.assessmentSession.dateTo) : null,
  );
  const [isDateToError, setIsDateToError] = useState<boolean>(false);
  const [sessionStatus, setSessionStatus] = useState<sessionStatusType>(
    props.data.assessmentSession.sessionStatus ? props.data.assessmentSession.sessionStatus : sessionStatusType.NEW,
  );
  const [isBlurName, setIsBlurName] = useState<boolean>(false);

  const isValidDateFormat = (date: Date): boolean => {
    const formattedDate = format(date, 'dd/MM/yyyy');
    if (formattedDate.match(/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}/)) {
      return true;
    } else return false;
  };

  const dateIsInvalid = (date: Date | null): boolean => {
    return !date?.toString().length || !isValidDateFormat(date);
  };

  const formatDate = (date: Date | null) => {
    if (date) {
      const newDate2 = new Date(date);
      newDate2.setHours(newDate2.getHours() + 1);
      const newDate = newDate2.toISOString().split('.')[0];
      return newDate;
    } else return;
  };

  const getSessionStatus = (statusId: sessionStatusType): string => {
    switch (statusId) {
      case sessionStatusType.NEW:
        return 'Nowa';
      case sessionStatusType.OPEN:
        return 'Otwarta';
      case sessionStatusType.BLOCKED:
        return 'Zawieszona';
      case sessionStatusType.CLOSED:
        return 'Zamknięta';
      default:
        return 'Nowa';
    }
  };

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isFieldEdited, setIsFieldEdited] = useState<boolean>(false);
  const [, setIsDateChanged] = useState<boolean>(false);

  const { isValidValue: validTaskGroupName } = useExamSessionValidation({
    isEdited: isFieldEdited,
    isAdd: props.workMode === WorkMode.ADD,
    inputValue: taskGroupName,
    data: props.data.name,
    verificationApi: useCallback(
      (value: string) => verifyDuplicateTaskGroupName(props.data.examSheetId, value),
      [props.data.examSheetId],
    ),
    onBlur: isBlurName,
  });

  const formatDatesToLocal = (date: Date) => {
    const dateToFormat = new Date(date);
    return `${dateToFormat.getFullYear()}-${('0' + (dateToFormat.getMonth() + 1).toString()).slice(-2)}-${(
      '0' + dateToFormat.getDate().toString()
    ).slice(-2)}`;
  };

  const saveChanges = () => {
    if (props.data.id !== NewTreeItemId.TaskGroup) {
      modifyExamTaskGroup(
        props.data.id,
        taskGroupName,
        taskGroupDateFrom ? formatDatesToLocal(taskGroupDateFrom) + 'T00:00:00' : new Date().toISOString(),
        taskGroupDateTo ? formatDatesToLocal(taskGroupDateTo) + 'T23:59:59' : new Date().toISOString(),
        sessionStatus,
      ).then(
        (result: AxiosResponse<IIdResult>) => {
          props.setWorkMode(WorkMode.VIEW);
          props.onSave(result.data, false);
          openAlert('success');
        },
        () => {
          openAlert('error');
        },
      );
    } else {
      const dateFrom = taskGroupDateFrom
        ? formatDatesToLocal(taskGroupDateFrom) + 'T00:00:00'
        : new Date().toISOString();
      const dateTo = taskGroupDateTo ? formatDatesToLocal(taskGroupDateTo) + 'T23:59:59' : new Date().toISOString();

      createExamTaskGroup(props.data.examSheetId, taskGroupName, dateFrom, dateTo).then(
        (result: AxiosResponse<IIdResult>) => {
          openAlert('success');
          props.setWorkMode(WorkMode.VIEW);
          props.onSave(result.data, true);
        },
        () => {
          openAlert('error');
        },
      );
    }
  };

  const isInvalidDateRange = (value?: Date | null, type?: string) => {
    if (
      value &&
      taskGroupDateFrom &&
      taskGroupDateTo &&
      isValidDateFormat(taskGroupDateFrom) &&
      isValidDateFormat(taskGroupDateTo)
    ) {
      const startTime = type === 'From' ? value.getTime() : taskGroupDateFrom.getTime();
      const endTime = type === 'From' ? taskGroupDateTo.getTime() : value.getTime();
      if (startTime > endTime) {
        return true;
      } else {
        return false;
      }
    } else return false;
  };

  useEffect(() => {
    const dateFrom = props.data.assessmentSession.dateFrom ? new Date(props.data.assessmentSession.dateFrom) : null;
    const dateTo = props.data.assessmentSession.dateTo ? new Date(props.data.assessmentSession.dateTo) : null;

    if (dateIsInvalid(taskGroupDateFrom) || isInvalidDateRange(taskGroupDateFrom, 'From')) {
      setIsDateFromError(true);
    } else {
      setIsDateFromError(false);
    }

    if (dateIsInvalid(taskGroupDateTo) || isInvalidDateRange(taskGroupDateTo, 'To')) {
      setIsDateToError(true);
    } else {
      setIsDateToError(false);
    }

    if (
      dateIsInvalid(taskGroupDateFrom) ||
      dateIsInvalid(taskGroupDateTo) ||
      isInvalidDateRange(taskGroupDateFrom, 'From') ||
      isInvalidDateRange(taskGroupDateTo, 'To')
    ) {
      setIsDisabled(true);
    } else setIsDisabled(false);

    if (!validTaskGroupName) {
      setIsDisabled(true);
      return;
    }

    if (!taskGroupName || !taskGroupName.length) {
      setIsDisabled(true);
      return;
    }

    if (
      taskGroupName === props.data.name &&
      formatDate(taskGroupDateFrom) === formatDate(dateFrom) &&
      formatDate(taskGroupDateTo) === formatDate(dateTo) &&
      sessionStatus === props.data.assessmentSession.sessionStatus
    ) {
      setIsDisabled(true);
      return;
    }
  }, [sessionStatus, validTaskGroupName, taskGroupName, taskGroupDateTo, taskGroupDateFrom, props.data]);

  const revert = () => {
    const dateFrom = new Date(props.data.assessmentSession.dateFrom);
    const dateTo = new Date(props.data.assessmentSession.dateTo);

    setTaskGroupName(props.data.name);
    setTaskGroupDateFrom(dateFrom);
    setTaskGroupDateTo(dateTo);
    setSessionStatus(props.data.assessmentSession.sessionStatus);
    setIsFieldEdited(false);
    setIsDateChanged(false);
    setIsDateToError(false);
    setIsDateFromError(false);
  };

  const closeHandle = () => {
    props.onClose(props.data, BreadcrumbLevel.EXAMSHEET, revert);
  };

  const handleDateChange = (date: Date | null, type?: string) => {
    if (date && !isNaN(date.getDate()) && type) {
      switch (type) {
        case 'From':
          setTaskGroupDateFrom(date);
          setIsDateFromError(false);
          break;
        case 'To':
          setTaskGroupDateTo(date);
          setIsDateToError(false);
          break;
      }
    } else {
      switch (type) {
        case 'From':
          setTaskGroupDateFrom(null);
          break;
        case 'To':
          setTaskGroupDateTo(null);
          break;
      }
    }
    setIsDateChanged(true);
  };

  const handleSessionGroupName = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setTaskGroupName(event.target.value.trimStart());
    setIsFieldEdited(true);
    setIsBlurName(false);
  };

  const handleSessionStatusChange = (event: any) => {
    if (event.target.value !== sessionStatusType.NEW) {
      setSessionStatus(event.target.value as number);
    }
  };

  const handleEditMode = () => {
    props.onEdit(props.data);
  };

  const editingDisabled = props.isReadOnly();
  const fieldsDisabled = props.workMode === WorkMode.VIEW || props.data.isExamSheetConfigured || editingDisabled;
  const dateFieldsDisabled =
    props.workMode === WorkMode.VIEW || sessionStatus === sessionStatusType.CLOSED || editingDisabled;

  const verifyIfSessionCanBeOpened = () => {
    showLoader(true, 'Weryfikuję dane');
    getCanSessionBeOpen(props.data.assessmentSession.id).then(
      (response: AxiosResponse<CanSessionBeSetToOpen>) => {
        setCanSessionBeOpen(response.data);
        setAssesmentGroupExist(response.data.sampleAssessmentGroupExist);
        setIsControlProccessAssigned(response.data.controlProcessIsOn);
        setCanSessionBeOpenWithZWO({
          sampleAssessmentGroupClosedSampleAssessmentSession:
            response.data.sampleAssessmentGroupClosedSampleAssessmentSession,
          allDeadlinesAppliedInAssessmentSession: response.data.allDeadlinesAppliedInAssessmentSession,
          limitsAreSetUp: response.data.limitsAreSetUp,
          examSheetIsConfigured: response.data.examSheetIsConfigured,
        });
        setCanSessionBeOpenNoZwo({
          allDeadlinesAppliedInAssessmentSession: response.data.allDeadlinesAppliedInAssessmentSession,
          limitsAreSetUp: response.data.limitsAreSetUp,
          examSheetIsConfigured: response.data.examSheetIsConfigured,
          sampleAssessmentGroupNotExistAndNoProcessesLimitIsSet:
            response.data.sampleAssessmentGroupNotExistAndNoProcessesLimitIsSet,
        });
        setCanSessionBeOpenWithControllProcess({
          atLeastOneToleranceConditionSetUp: response.data.atLeastOneToleranceConditionSetUp,
          countOfProcess: response.data.frequencyControlTasksSetUp && response.data.percentageControlTasksSetUp,
          atLeastOneParameterIsSetUp: response.data.atLeastOneParameterIsSetUp,
        });
        showLoader(false);
        openAlert('success', 'Zweryfikowano dane');
      },
      () => {
        showLoader(false);
        openAlert('error');
      },
    );
  };

  const verifyIfSessionCanBeClosed = () => {
    showLoader(true, 'Weryfikuję dane');

    getCanSessionBeClosed(props.data.assessmentSession.id).then(
      (response: AxiosResponse<CanSessionBeSetToClosed>) => {
        setCanSessionBeClosed(response.data);
        showLoader(false);
        openAlert('success', 'Zweryfikowano dane');
      },
      () => {
        showLoader(false);
        openAlert('error');
      },
    );
  };

  useEffect(() => {
    let result = true;
    if (canSessionBeOpenWithZWO) {
      let key: keyof CanSessionBeSetToOpenWithZwo;
      for (key in canSessionBeOpenWithZWO) {
        result = result && canSessionBeOpenWithZWO[key];
      }
    }
    setAreAllCasesTrue(result);
  }, [canSessionBeOpenWithZWO]);

  useEffect(() => {
    let result = true;
    if (canSessionBeOpenNoZWO) {
      let key: keyof CanSessionBeSetToOpenNoZwo;
      for (key in canSessionBeOpenNoZWO) {
        result = result && canSessionBeOpenNoZWO[key];
      }
    }
    setAreAllCasesTrueNoZwo(result);
  }, [canSessionBeOpenNoZWO]);

  useEffect(() => {
    let result = true;
    if (canSessionBeOpenWithControllProcess) {
      let key: keyof isControllProcess;
      for (key in canSessionBeOpenWithControllProcess) {
        result = result && canSessionBeOpenWithControllProcess[key];
      }
    }
    setAreAllCasesTrueControllProcess(result);
  }, [canSessionBeOpenWithControllProcess]);

  const isSessionValidToBeClosed = useMemo(() => {
    let result = true;
    if (canSessionBeClosed) {
      let key: keyof CanSessionBeSetToClosed;
      for (key in canSessionBeClosed) {
        result = result && canSessionBeClosed[key];
      }
    }
    return result;
  }, [canSessionBeClosed]);

  const handlePromptOpen = (value: sessionStatusType) => {
    const prevSessionStatus = props.data.assessmentSession.sessionStatus;
    if (value === sessionStatusType.OPEN && prevSessionStatus === sessionStatusType.NEW) {
      verifyIfSessionCanBeOpened();
    } else if (
      value === sessionStatusType.CLOSED &&
      (prevSessionStatus === sessionStatusType.OPEN || prevSessionStatus === sessionStatusType.BLOCKED)
    ) {
      verifyIfSessionCanBeClosed();
    } else saveChanges();
  };

  const handleConfirmWithZwo = () => {
    if (areAllCasesTrue) {
      saveChanges();
      setCanSessionBeOpenWithZWO(undefined);
    } else {
      setCanSessionBeOpenWithZWO(undefined);
    }
  };

  const handleConfirmNoZwo = () => {
    if (areAllCasesTrueNoZwo) {
      saveChanges();
    }
    setCanSessionBeOpenNoZwo(undefined);
  };

  const handleConfirmClosingSession = () => {
    if (isSessionValidToBeClosed) {
      saveChanges();
    }
    setCanSessionBeClosed(undefined);
  };

  return (
    <>
      {props.data && (
        <Grid container alignContent="space-between" style={{ height: '490px' }}>
          <Grid container justifyContent="flex-end" alignContent="center">
            {props.workMode !== WorkMode.EDIT ? (
              <EditButton workMode={props.workMode} handleEditMode={handleEditMode} isDisabled={props.isReadOnly()} />
            ) : (
              <DeleteButton
                workMode={props.workMode}
                handleDeleteMode={props.onDelete}
                isDisabled={props.isReadOnly()}
              />
            )}
          </Grid>
          <Grid container justifyContent="center" alignContent="center">
            <Grid container justifyContent="center" className={sessionDetailsClasses.headerRow}>
              {props.workMode === WorkMode.VIEW && <div className={sessionDetailsClasses.headerText}>Grupa zadań</div>}
              {props.workMode === WorkMode.EDIT && (
                <div className={sessionDetailsClasses.headerText}>Edycja grupy zadań</div>
              )}
              {props.workMode === WorkMode.ADD && (
                <div className={sessionDetailsClasses.headerText}>Zdefiniuj grupę zadań</div>
              )}
            </Grid>

            <Grid container justifyContent="center">
              <TextField
                className={sessionDetailsClasses.formInput}
                style={{ width: '380px' }}
                value={taskGroupName}
                onChange={handleSessionGroupName}
                onBlur={() => {
                  setIsBlurName(true);
                }}
                id="outlined"
                label="Nazwa"
                variant="outlined"
                disabled={fieldsDisabled}
                error={props.workMode !== WorkMode.VIEW && !validTaskGroupName}
                inputProps={{
                  style: {
                    padding: '15.5px',
                    color: '#2C2D3E',
                  },
                }}
                InputProps={{
                  startAdornment: blockIconForInput(fieldsDisabled),
                }}
                InputLabelProps={{
                  style: { color: '#2C2D3E' },
                }}
                FormHelperTextProps={{
                  style: {
                    fontSize: '11px',
                    color: '#BC0000',
                  },
                }}
                helperText={
                  props.workMode !== WorkMode.VIEW && !validTaskGroupName && validTaskGroupName !== undefined
                    ? 'Pole "Nazwa" musi być unikatowe dla sesji egzaminacyjnej'
                    : undefined
                }
              />
              <Grid container justifyContent="flex-start" className={classes.subHeaderRow}>
                {props.workMode === WorkMode.VIEW && <div className={classes.subHeaderText}>Sesja oceniania</div>}
                {props.workMode === WorkMode.EDIT && (
                  <div className={classes.subHeaderText}>Edycja sesji oceniania</div>
                )}
                {props.workMode === WorkMode.ADD && (
                  <div className={classes.subHeaderText}>Zdefiniuj sesję oceniania</div>
                )}
              </Grid>
              <Grid container justifyContent="center">
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={plLocale}>
                  <div style={{ margin: '10px' }}>
                    <DatePicker
                      inputFormat="dd/MM/yyyy"
                      minDate={new Date(1900, 0)}
                      maxDate={taskGroupDateTo ? taskGroupDateTo : undefined}
                      onChange={(e: Date | null) => handleDateChange(e, 'From')}
                      value={taskGroupDateFrom}
                      label={'Termin rozpoczęcia'}
                      views={['year', 'month', 'day']}
                      disabled={dateFieldsDisabled}
                      InputProps={{
                        startAdornment: blockIconForInput(dateFieldsDisabled, { marginRight: '0px' }),
                      }}
                      renderInput={(params) => (
                        <DateTextField
                          {...params}
                          style={{ width: '190px' }}
                          error={isDateFromError}
                          helperText={isDateFromError && 'Nieprawidłowa data'}
                        />
                      )}
                    />
                    <DatePicker
                      inputFormat="dd/MM/yyyy"
                      minDate={taskGroupDateFrom ? taskGroupDateFrom : undefined}
                      onChange={(e: Date | null) => handleDateChange(e, 'To')}
                      value={taskGroupDateTo}
                      label={'Termin zakończenia'}
                      views={['year', 'month', 'day']}
                      disabled={dateFieldsDisabled}
                      InputProps={{
                        startAdornment: blockIconForInput(dateFieldsDisabled, { marginRight: '0px' }),
                      }}
                      renderInput={(params) => (
                        <DateTextField
                          {...params}
                          style={{ width: '190px' }}
                          error={isDateToError}
                          helperText={isDateToError && 'Nieprawidłowa data'}
                        />
                      )}
                    />
                  </div>
                </LocalizationProvider>
                <Grid container justifyContent="flex-start" direction="column" className={classes.sessionStatusRow}>
                  {props.workMode !== WorkMode.EDIT && (
                    <>
                      <div className={classes.subHeader}>Status sesji oceniania:</div>
                      <div className={classes.sessionStatus}>{getSessionStatus(sessionStatus)}</div>
                    </>
                  )}
                </Grid>
                {props.workMode === WorkMode.EDIT && (
                  <FormControl className={sessionDetailsClasses.formInput} style={{ width: '380px' }}>
                    <InputLabel id="Status sesji oceniania*" style={{ color: '#2C2D3E', left: '14px', top: '-6px' }}>
                      Status sesji oceniania
                    </InputLabel>
                    <Select
                      variant="outlined"
                      id="outlined"
                      labelId="Status sesji oceniania"
                      label="Status sesji oceniania"
                      value={sessionStatus}
                      onChange={handleSessionStatusChange}
                      inputProps={{
                        style: {
                          padding: '10px',
                          color: '#2C2D3E',
                        },
                      }}
                    >
                      <MenuItem
                        disableRipple
                        value={sessionStatusType.NEW}
                        disabled={sessionStatus !== sessionStatusType.NEW}
                      >
                        Nowa
                      </MenuItem>
                      <MenuItem disableRipple value={sessionStatusType.OPEN}>
                        Otwarta
                      </MenuItem>
                      <MenuItem disableRipple value={sessionStatusType.BLOCKED}>
                        Zawieszona
                      </MenuItem>
                      <MenuItem disableRipple value={sessionStatusType.CLOSED}>
                        Zamknięta
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end">
            {props.workMode === WorkMode.VIEW && (
              <AddSessionItemButton
                caption="Dodaj zadanie"
                disabled={props.isSessionImported || props.data.isExamSheetConfigured}
                onClick={() =>
                  props.onAddChild(BreadcrumbLevel.TASK, props.data.id, props.data.parentId ?? props.data.examSheetId)
                }
              />
            )}
            {props.workMode !== WorkMode.VIEW && (
              <ActionButtons
                disable={isDisabled}
                saveHandle={() => handlePromptOpen(sessionStatus)}
                closeHandle={closeHandle}
              />
            )}
          </Grid>
        </Grid>
      )}

      {canSessionBeOpenWithZWO && assesmentGroupExist && (
        <Prompt
          type={'warning'}
          open={true}
          size="sm"
          mainActionButtonText={
            (isControlProccessAssigned && areAllCasesTrue && areAllCasesTrueControllProcess) ||
            (!isControlProccessAssigned && areAllCasesTrue)
              ? 'POTWIERDŹ'
              : undefined
          }
          onCancel={areAllCasesTrue ? () => setCanSessionBeOpenWithZWO(undefined) : undefined}
          onConfirm={handleConfirmWithZwo}
          mainText="By móc zmienić stan sesji z Nowej na Otwartą, muszą zostać spełnione poniższe kryteria:"
          subTexts={[
            {
              text: (
                <div>
                  <ul className={classes.sessionStateChangeConditionsList}>
                    {sessionValidationMessagesWithZwo.map(
                      (message: sessionValidationMessage<CanSessionBeSetToOpenWithZwo>) => {
                        return (
                          <li key={message.propName}>
                            {canSessionBeOpenWithZWO && canSessionBeOpenWithZWO[message.propName] ? (
                              <CheckCircleIcon className={classes.successIcon} />
                            ) : (
                              <CancelIcon className={classes.errorIcon} />
                            )}
                            {message.message}
                          </li>
                        );
                      },
                    )}
                  </ul>
                  {isControlProccessAssigned && (
                    <div>
                      <p style={{ fontWeight: '600' }}>Ustawione zostały parametry procesu kontrolnego:</p>
                      <ul className={classes.sessionStateChangeConditionsList}>
                        {isControllProcessMessage.map((message: sessionValidationMessage<isControllProcess>) => {
                          return (
                            <li key={message.propName}>
                              {canSessionBeOpenWithControllProcess &&
                              canSessionBeOpenWithControllProcess[message.propName] ? (
                                <CheckCircleIcon className={classes.successIcon} />
                              ) : (
                                <CancelIcon className={classes.errorIcon} />
                              )}
                              {message.message}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
      {canSessionBeOpenNoZWO && !assesmentGroupExist && (
        <Prompt
          type={'warning'}
          open={true}
          size="sm"
          mainActionButtonText={areAllCasesTrueNoZwo ? 'POTWIERDŹ' : undefined}
          onCancel={areAllCasesTrueNoZwo ? () => setCanSessionBeOpenNoZwo(undefined) : undefined}
          onConfirm={handleConfirmNoZwo}
          mainText="By móc zmienić stan sesji z Nowej na Otwartą, muszą zostać spełnione poniższe kryteria:"
          subTexts={[
            {
              text: (
                <div>
                  <ul className={classes.sessionStateChangeConditionsList}>
                    {sessionValidationMessagesNoZwo.map(
                      (message: sessionValidationMessage<CanSessionBeSetToOpenNoZwo>) => {
                        return (
                          <li key={message.propName}>
                            {canSessionBeOpenNoZWO && canSessionBeOpenNoZWO[message.propName] ? (
                              <CheckCircleIcon className={classes.successIcon} />
                            ) : (
                              <CancelIcon className={classes.errorIcon} />
                            )}
                            {message.message}
                          </li>
                        );
                      },
                    )}
                  </ul>
                </div>
              ),
            },
          ]}
        />
      )}
      {canSessionBeClosed && (
        <Prompt
          type={'warning'}
          open={true}
          size="sm"
          mainActionButtonText={isSessionValidToBeClosed ? 'POTWIERDŹ' : undefined}
          onCancel={isSessionValidToBeClosed ? () => setCanSessionBeClosed(undefined) : undefined}
          onConfirm={handleConfirmClosingSession}
          mainText="By móc zmienić stan sesji na Zamkniętą, muszą zostać spełnione poniższe kryteria:"
          subTexts={[
            {
              text: (
                <ul className={classes.sessionStateChangeConditionsList}>
                  {sessionToBeClosedValidationMessages.map(
                    (message: sessionValidationMessage<CanSessionBeSetToClosed>) => {
                      return (
                        <li key={message.propName}>
                          {canSessionBeClosed[message.propName] ? (
                            <CheckCircleIcon className={classes.successIcon} />
                          ) : (
                            <CancelIcon className={classes.errorIcon} />
                          )}
                          {message.message}
                        </li>
                      );
                    },
                  )}
                </ul>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

export default TaskGroupDetails;
